import React, { useState, useEffect } from 'react';
import { NotificationManager } from 'react-notifications';
import useLoading from '../../../hooks/useLoading';
import {
    apiGetPronosticoConfig,
    apiAddPronosticoConfig,
    apiDeletePronosticoConfig
} from '../../../lib/apiPaseLista';
import { apiGetCultivos, apiGetActividades } from '../../../lib/apiSys21';

const ConfiguracionPronosticos = () => {
    const loading = useLoading();
    const [configuraciones, setConfiguraciones] = useState([]);

    const [cultivos, setCultivos] = useState([]);
    const [actividades, setActividades] = useState([]);

    const [form, setForm] = useState({
        id_cultivo: '',
        id_actividad: '',
        tipo: 'regular'
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const cargarDatos = async () => {
        loading.set();
        try {
            const resConfig = await apiGetPronosticoConfig();
            if (!resConfig.error) {
                setConfiguraciones(Array.isArray(resConfig) ? resConfig : (resConfig.data || []));
            }

            const resCultivos = await apiGetCultivos();
            const resActividades = await apiGetActividades();
            setCultivos(Array.isArray(resCultivos) ? resCultivos : (resCultivos.data || resCultivos.rows || []));
            setActividades(Array.isArray(resActividades) ? resActividades : (resActividades.data || resActividades.rows || []));
        } catch (error) {
            console.error(error);
            NotificationManager.error('Error al cargar la información', '', 5000);
        }
        loading.stop();
    };

    useEffect(() => {
        cargarDatos();
        // eslint-disable-next-line
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let nuevoFormulario = { ...form, [name]: value };

        if (name === 'id_cultivo') {
            nuevoFormulario.id_actividad = '';
            nuevoFormulario.tipo = 'regular';
            setCurrentPage(1);
        }

        if (name === 'id_actividad' && value !== "") {
            // 🟢 OBLIGATORIO: Buscamos estrictamente por sys21_id
            const actividadSeleccionada = actividades.find(a =>
                a.sys21_id && a.sys21_id.toString() === value.toString()
            );

            if (actividadSeleccionada) {
                const importeDestajo = parseFloat(actividadSeleccionada.destajo || actividadSeleccionada.importe_destajo || 0);
                nuevoFormulario.tipo = importeDestajo > 0 ? 'destajo' : 'regular';
            }
        } else if (name === 'id_actividad' && value === "") {
            nuevoFormulario.tipo = 'regular';
        }

        setForm(nuevoFormulario);
    };

    const handleAgregar = async (e) => {
        e.preventDefault();
        if (!form.id_cultivo || !form.id_actividad) {
            return NotificationManager.warning('Seleccione Cultivo y Actividad', '', 3000);
        }

        loading.set();
        try {
            const resp = await apiAddPronosticoConfig(form);
            if (resp.error) {
                NotificationManager.error(resp.message || 'Error al guardar', '', 5000);
            } else {
                NotificationManager.success('Configuración guardada', '', 3000);
                setForm({ ...form, id_actividad: '', tipo: 'regular' });
                cargarDatos();
            }
        } catch (error) {
            NotificationManager.error('Error interno', '', 5000);
        }
        loading.stop();
    };

    const handleEliminar = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar esta configuración?')) return;

        loading.set();
        try {
            const resp = await apiDeletePronosticoConfig(id);
            if (resp.error) {
                NotificationManager.error(resp.message || 'Error al eliminar', '', 5000);
            } else {
                NotificationManager.success('Eliminado correctamente', '', 3000);
                cargarDatos();
            }
        } catch (error) {
            NotificationManager.error('Error interno', '', 5000);
        }
        loading.stop();
    };

    // 🟢 LÓGICA DE FILTRADO DE LA TABLA
    const filteredConfiguraciones = configuraciones.filter(conf => {

        // 1. NUEVO: Filtro automático por el Select de Cultivo
        if (form.id_cultivo && conf.id_cultivo?.toString() !== form.id_cultivo.toString()) {
            return false; // Si hay un cultivo seleccionado y este no es, lo ocultamos
        }

        // 2. Filtro por el buscador de texto
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();

        const matchCultivo = conf.cultivo?.nombre?.toLowerCase().includes(term);
        const matchCultivoId = conf.id_cultivo?.toString().includes(term);

        const matchActividad = conf.actividad?.nombre?.toLowerCase().includes(term);
        const matchActividadId = conf.id_actividad?.toString().includes(term);

        const matchTipo = conf.tipo?.toLowerCase().includes(term);

        return matchCultivo || matchCultivoId || matchActividad || matchActividadId || matchTipo;
    });

    // 🟢 LÓGICA DE PAGINACIÓN DE LA TABLA
    const totalPages = Math.ceil(filteredConfiguraciones.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredConfiguraciones.slice(indexOfFirstItem, indexOfLastItem);

    // 🟢 AQUÍ VA LA LÓGICA DE LAS ACTIVIDADES DISPONIBLES (Fuera del filter)
    // 1. Identificar las actividades que YA están configuradas para el cultivo seleccionado
    const actividadesYaConfiguradas = configuraciones
        .filter(conf => conf.id_cultivo?.toString() === form.id_cultivo?.toString())
        .map(conf => conf.id_actividad?.toString());

    // 2. Filtrar el catálogo global restando las que ya están en la tabla
    const actividadesDisponibles = form.id_cultivo
        ? actividades.filter(a => {
            // 🟢 OBLIGATORIO: Filtramos estrictamente por sys21_id
            if (!a.sys21_id) return false;
            const idAct = a.sys21_id.toString();
            return !actividadesYaConfiguradas.includes(idAct);
        })
        : []; // Si no hay cultivo seleccionado, la lista está vacía

    return (
        <div className="card shadow-sm">
            <div className="card-header bg-white">
                <h5 className="mb-0 text-primary">
                    <i className="fas fa-cogs mr-2" />
                    Configuración de Formato de Pronósticos
                </h5>
            </div>

            <div className="card-body">
                {/* FORMULARIO DE AGREGAR */}
                <form onSubmit={handleAgregar} className="row align-items-end mb-4 bg-light p-3 rounded border mx-0">
                    <div className="col-md-3">
                        <label className="font-weight-bold text-muted small">Cultivo</label>
                        <select className="form-control" name="id_cultivo" value={form.id_cultivo} onChange={handleInputChange}>
                            <option value="">Seleccione...</option>
                            {/* 🟢 Renderizamos usando sys21_id */}
                            {cultivos.map(c => {
                                if (!c.sys21_id) return null;
                                return <option key={c.sys21_id} value={c.sys21_id}>{c.sys21_id} - {c.nombre}</option>;
                            })}
                        </select>
                    </div>

                    <div className="col-md-4">
                        <label className="font-weight-bold text-muted small">Actividad</label>
                        <select
                            className={`form-control ${!form.id_cultivo ? 'bg-light' : ''}`}
                            name="id_actividad"
                            value={form.id_actividad}
                            onChange={handleInputChange}
                            disabled={!form.id_cultivo}
                        >
                            <option value="">
                                {form.id_cultivo ? 'Seleccione...' : '← Seleccione un cultivo primero'}
                            </option>
                            {/* 🟢 Renderizamos usando sys21_id */}
                            {actividadesDisponibles.map(a => {
                                if (!a.sys21_id) return null;
                                return <option key={a.sys21_id} value={a.sys21_id}>{a.sys21_id} - {a.nombre}</option>;
                            })}
                        </select>
                    </div>

                    <div className="col-md-3">
                        <label className="font-weight-bold text-muted small">Tipo de Pago</label>
                        <select className="form-control bg-white" name="tipo" value={form.tipo} disabled>
                            <option value="regular">Regular</option>
                            <option value="destajo">Destajo</option>
                        </select>
                    </div>

                    <div className="col-md-2 mt-3 mt-md-0">
                        <button type="submit" className="btn btn-success w-100">
                            <i className="fas fa-plus mr-1" /> Agregar
                        </button>
                    </div>
                </form>

                {/* BUSCADOR */}
                <div className="row mb-3">
                    <div className="col-md-6 ml-auto">
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text bg-white"><i className="fas fa-search text-muted" /></span>
                            </div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Buscar por ID, cultivo, actividad o tipo..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* TABLA DE CONFIGURACIONES */}
                <div className="table-responsive">
                    <table className="table table-hover table-bordered table-sm text-center mb-0">
                        <thead className="thead-light">
                            <tr>
                                <th>Cultivo</th>
                                <th>Actividad</th>
                                <th>Tipo</th>
                                <th style={{ width: '100px' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="text-muted py-4">No se encontraron registros</td>
                                </tr>
                            ) : (
                                currentItems.map(conf => (
                                    <tr key={conf.id}>
                                        {/* 🟢 Mostramos "ID - Nombre" en la Tabla */}
                                        <td className="align-middle text-left font-weight-bold">
                                            <span className="text-secondary mr-1">#{conf.id_cultivo}</span>
                                            {conf.cultivo?.nombre}
                                        </td>
                                        <td className="align-middle text-left">
                                            <span className="text-secondary mr-1">#{conf.id_actividad}</span>
                                            {conf.actividad?.nombre}
                                        </td>
                                        <td className="align-middle">
                                            <span className={`badge ${conf.tipo === 'destajo' ? 'badge-info' : 'badge-secondary'}`}>
                                                {conf.tipo.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="align-middle">
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => handleEliminar(conf.id)}
                                                title="Eliminar"
                                            >
                                                <i className="fas fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* CONTROLES DE PAGINACIÓN */}
                {filteredConfiguraciones.length > 0 && (
                    <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
                        <span className="text-muted small mb-2 mb-md-0">
                            Mostrando {indexOfFirstItem + 1} al {Math.min(indexOfLastItem, filteredConfiguraciones.length)} de {filteredConfiguraciones.length} registros
                        </span>
                        <nav>
                            <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}>
                                        Anterior
                                    </button>
                                </li>
                                <li className="page-item disabled">
                                    <span className="page-link text-dark font-weight-bold">
                                        Página {currentPage} de {totalPages}
                                    </span>
                                </li>
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}>
                                        Siguiente
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}

            </div>
        </div>
    );
};

export default ConfiguracionPronosticos;